import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import axios from 'axios'
import { useAuth } from './AuthContext'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface KycData {
  verified: boolean
  submitted: boolean  // ส่งข้อมูล KYC แล้วหรือยัง
  status: 'not_started' | 'pending' | 'approved' | 'rejected'
  verifiedType: 'individual' | 'company' | null
  verifiedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  kycData: {
    // Individual
    prefix?: string
    firstName?: string
    lastName?: string
    idCard?: string
    address?: string
    district?: string
    subDistrict?: string
    province?: string
    postalCode?: string
    useAddressForTax?: boolean
    taxInvoiceDeliveryAddress?: string
    taxInvoiceDeliveryDistrict?: string
    taxInvoiceDeliverySubDistrict?: string
    taxInvoiceDeliveryProvince?: string
    taxInvoiceDeliveryPostalCode?: string
    // Company
    companyName?: string
    taxId?: string
    businessType?: string
    contactPerson?: string
    branch?: string
    // Status fields
    status?: string
    rejectedAt?: string
    rejectionReason?: string
  } | null
}

interface KycContextType {
  kycStatus: KycData | null
  loading: boolean
  requiresKyc: boolean
  checkKycStatus: () => Promise<void>
  submitIndividualKyc: (data: any) => Promise<void>
  submitCompanyKyc: (data: any) => Promise<void>
}

const KycContext = createContext<KycContextType | undefined>(undefined)

export function KycProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [kycStatus, setKycStatus] = useState<KycData | null>(null)
  const [loading, setLoading] = useState(true)
  const [requiresKyc, setRequiresKyc] = useState(false)

  // Check KYC status when user logs in
  useEffect(() => {
    if (user) {
      checkKycStatus()
    } else {
      setKycStatus(null)
      setLoading(false)
      setRequiresKyc(false)
    }
  }, [user])

  const checkKycStatus = async () => {
    if (!user) return

    try {
      setLoading(true)
      const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken')
      const response = await axios.get(`${API_BASE_URL}/api/auth/verify/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      const data: KycData = response.data.data
      setKycStatus(data)
      
      console.log('🔍 KYC Status Check:', {
        verified: data.verified,
        submitted: data.submitted,
        status: data.status,
        requiresKyc: !data.verified
      })
      
      // Hard-lock policy: require KYC gate for all non-approved statuses
      // (not_started, pending, rejected). Only approved users can pass.
      setRequiresKyc(!data.verified)
    } catch (error) {
      console.error('Failed to check KYC status:', error)
      setRequiresKyc(true) // Assume KYC required if check fails
    } finally {
      setLoading(false)
    }
  }

  const submitIndividualKyc = async (data: any) => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken')
      await axios.post(`${API_BASE_URL}/api/auth/verify/individual`, data, {
        headers: { Authorization: `Bearer ${token}` }
      })
      // Refresh KYC status after submission
      await checkKycStatus()
    } catch (error) {
      throw error
    }
  }

  const submitCompanyKyc = async (data: any) => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken')
      await axios.post(`${API_BASE_URL}/api/auth/verify/company`, data, {
        headers: { Authorization: `Bearer ${token}` }
      })
      // Refresh KYC status after submission
      await checkKycStatus()
    } catch (error) {
      throw error
    }
  }

  return (
    <KycContext.Provider value={{
      kycStatus,
      loading,
      requiresKyc,
      checkKycStatus,
      submitIndividualKyc,
      submitCompanyKyc
    }}>
      {children}
    </KycContext.Provider>
  )
}

export const useKyc = () => {
  const context = useContext(KycContext)
  if (context === undefined) {
    throw new Error('useKyc must be used within KycProvider')
  }
  return context
}
